import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";


export default function SslPanel({ appId, app, socketCheck }) {
    const [check, setCheck] = useState(null);
    const [domain, setDomain] = useState(app?.config?.domain || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/apps/${appId}/ssl`, { credentials: "include" });
                if (r.ok) setCheck((await r.json()).check);
            } catch { /* stream will fill it */ }
        })();
    }, [appId]);

    useEffect(() => {
        if (socketCheck) setCheck(socketCheck);
    }, [socketCheck]);

    const save = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/apps/${appId}/config`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ config: { domain: domain.trim() } }),
            });
            if (!r.ok) throw new Error();
            toast.success("Domain saved — first check is running");
        } catch {
            toast.error("Could not save domain");
        } finally {
            setSaving(false);
        }
    };

    const days = check?.days_left;
    const tone =
        check?.error || days === null || days === undefined
            ? "text-slate-500"
            : days < 0
              ? "text-rose-400"
              : days <= 14
                ? "text-amber-400"
                : "text-emerald-400";

    return (
        <div data-testid="ssl-panel" className="glass-panel rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <ShieldCheck className="h-4 w-4 text-teal-400" />
                    SSL certificate
                </h3>
                <div className="flex items-center gap-2">
                    <input
                        data-testid="ssl-domain-input"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        placeholder="example.com"
                        className="w-52 rounded-md border border-slate-800 bg-slate-950/70 px-2.5 py-1.5 font-mono text-[11px] text-slate-200 placeholder:text-slate-600 focus:border-teal-500/60 focus:outline-none"
                    />
                    <button
                        data-testid="ssl-save-button"
                        onClick={save}
                        disabled={saving || !domain.trim()}
                        className="rounded-md bg-teal-500 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-950 transition-colors hover:bg-teal-400 disabled:opacity-50"
                    >
                        {saving ? "saving…" : "watch it"}
                    </button>
                </div>
            </div>

            {!check && (
                <p
                    data-testid="ssl-empty"
                    className="mt-4 rounded-md border border-dashed border-slate-800 px-3 py-4 text-center font-mono text-[10.5px] text-slate-600"
                >
                    Set a domain above — MDH checks its certificate hourly and warns you 14 days before expiry.
                </p>
            )}

            {check && (
                <div className="mt-4 flex flex-wrap items-end justify-between gap-4 rounded-lg border border-slate-800/70 bg-slate-900/50 px-4 py-4">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            {check.domain} · days until expiry
                        </p>
                        <p data-testid="ssl-days" className={`mt-1 font-display text-4xl font-bold ${tone}`}>
                            {check.error ? "error" : days < 0 ? "expired" : days}
                        </p>
                        <p className="mt-1 font-mono text-[10px] text-slate-500">
                            {check.error
                                ? check.error
                                : `expires ${check.expires_at ? check.expires_at.slice(0, 10) : "—"} · issuer ${check.issuer || "—"}`}
                        </p>
                    </div>
                    <p className="font-mono text-[9.5px] text-slate-600">
                        checked {check.checked_at ? check.checked_at.slice(11, 19) : "—"} UTC · hourly
                    </p>
                </div>
            )}
        </div>
    );
}
